import React, { useState, useEffect } from "react";
import apiCall from "./../../API/API";
import { toast } from "react-toastify";
import { CiCirclePlus } from "react-icons/ci";
import { Row, Col, Container, Button, Card, Form, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

const SpinGameSettings = () => {
  // Daily Limit Form States
  const [spinLimit, setSpinLimit] = useState(5);
  
  // Game Options Form States
  const [gameOptions, setGameOptions] = useState([]);
  const [newOption, setNewOption] = useState({
    value: "",
    label: "",
    color: "#A9CCE3"
  });
  
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);
  const dispatch = useDispatch();
  
  const fetchAdvanceInfo = async () => {
    try {
      setLoading(true);
      const { data, error } = await apiCall("GET", "/get-advance-info");
      if (data) {
        console.log("Advance info data:", data);
        // Extract spin game data
        const spinGameData = data?.data?.spin_game || {};
        setSpinLimit(spinGameData?.spin_limit || 5);
        console.log("Current spin game data:", spinGameData);
      }
    } catch (e) {
      console.log(e.message);
      toast.error("Failed to fetch advance info");
    } finally {
      setLoading(false);
    }
  };

  const fetchGameOptions = async () => {
    try {
      setOptionsLoading(true);
      const { data, error } = await apiCall("GET", "/get_game_option");
      if (data) {
        console.log("Game options data:", data);
        setGameOptions(data?.data || []);
      }
    } catch (e) {
      console.log(e.message);
      toast.error("Failed to fetch game options");
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvanceInfo();
    fetchGameOptions();
  }, []);

  const handleUpdateDailyLimit = async () => {
    try {
      setLoading(true);
      const body = { 
        dailyLimit: spinLimit
      };
      console.log("Update body:", body);
      const {data, error} = await apiCall("POST", `/update-spin-dailyLimit`, body);
      if (error) {
        toast.error(error.message || "An error occurred");
      } else {
        toast.success(data.message || "Spin limit updated successfully!");
        fetchAdvanceInfo(); // Refresh the data after update
      }
    } catch (error) {
      console.log("error------", error);
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleAddOption = async () => {
    try {
      setOptionsLoading(true);
      const { value, label, color } = newOption;
      const body = { value, label, color };
      console.log("Add option body:", body);
      const {data, error} = await apiCall("POST", `/add-spin-options`, body);
      if (error) {
        toast.error(error.message || "An error occurred");
      } else {
        toast.success(data.message || "Game option added successfully!");
        setNewOption({
          value: "",
          label: "",
          color: "#A9CCE3"
        });
        fetchGameOptions(); // Refresh the data after update
      }
    } catch (error) {
      console.log("error------", error);
      toast.error(error.message || "An error occurred");
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleDeleteClick = (option) => {
    setOptionToDelete(option);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!optionToDelete) return;
    
    try {
      setOptionsLoading(true);
      const body = { optionId: optionToDelete.optionId };
      const {data, error} = await apiCall("POST", `/delete-spin-option`, body);
      if (error) {
        toast.error(error.message || "An error occurred");
      } else {
        toast.success(data.message || "Game option deleted successfully!");
        fetchGameOptions(); // Refresh the data after update
      }
    } catch (error) {
      console.log("error------", error);
      toast.error(error.message || "An error occurred");
    } finally {
      setOptionsLoading(false);
      setShowDeleteModal(false);
      setOptionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setOptionToDelete(null);
  };

  return (
    <React.Fragment>
      <Container>
        <div className="bonanza-settings">
          {/* Daily Limit Form */}
          <Card className="mb-4">
            <Card.Header>
              <h2>Spin Game Daily Limit Settings</h2>
            </Card.Header>
            <Card.Body>
              <Row className="align-items-center">
                <Col md={6}>
                  <Form.Group className="mb-0">
                    <Form.Control
                      type="number"
                      placeholder="Enter Spin Limit"
                      value={spinLimit || ""}
                      onChange={(e) => setSpinLimit(e.target.value)}
                      disabled={loading}
                      style={{ width: '200px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <div className="d-flex justify-content-start">
                    <Button 
                      variant="primary" 
                      onClick={handleUpdateDailyLimit}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update Spin Limit"}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Game Options Form */}
          <Card>
            <Card.Header>
              <h2>Game Options Management</h2>
            </Card.Header>
            <Card.Body>
              {/* Add New Option Form */}
              <Card className="mb-3">
                <Card.Header>
                  <h5>Add New Game Option</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Value:</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="e.g., 99, 299"
                          value={newOption.value}
                          onChange={(e) => setNewOption({...newOption, value: e.target.value})}
                          disabled={optionsLoading}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Label:</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g., 99 points"
                          value={newOption.label}
                          onChange={(e) => setNewOption({...newOption, label: e.target.value})}
                          disabled={optionsLoading}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Color:</Form.Label>
                        <Form.Control
                          type="color"
                          value={newOption.color}
                          onChange={(e) => setNewOption({...newOption, color: e.target.value})}
                          disabled={optionsLoading}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button 
                    variant="success" 
                    onClick={handleAddOption}
                    disabled={optionsLoading || !newOption.value || !newOption.label}
                  >
                    {optionsLoading ? "Adding..." : "Add Option"}
                  </Button>
                </Card.Body>
              </Card>

              {/* Display Existing Options */}
              <div>
                <h5>Existing Game Options</h5>
                {optionsLoading ? (
                  <p>Loading options...</p>
                ) : (
                  <Row>
                    {gameOptions.map((option, index) => (
                      <Col md={4} key={option._id || index} className="mb-3">
                        <Card>
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <h6>{option.label}</h6>
                                <p className="mb-1">Value: {option.value}</p>
                                <div 
                                  className="color-preview" 
                                  style={{
                                    width: '20px', 
                                    height: '20px', 
                                    backgroundColor: option.color,
                                    border: '1px solid #ccc',
                                    display: 'inline-block'
                                  }}
                                ></div>
                              </div>
                              <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => handleDeleteClick(option)}
                                disabled={optionsLoading}
                              >
                                Delete
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div 
              className="color-preview mx-auto mb-3" 
              style={{
                width: '50px', 
                height: '50px', 
                backgroundColor: optionToDelete?.color || '#A9CCE3',
                border: '2px solid #dee2e6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              {optionToDelete?.value}
            </div>
            <h5>{optionToDelete?.label}</h5>
            <p className="text-muted">
              Are you sure you want to delete this game option? This action cannot be undone.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel} disabled={optionsLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={optionsLoading}>
            {optionsLoading ? "Deleting..." : "Delete Option"}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default SpinGameSettings;